// =============================================================================
// client/src/pages/campaigns/CampaignContacts.jsx | v2.0 | SQ-Chrome S1 | P9b — W-1, W-2
// =============================================================================
// v2.0 (SQ-Chrome S1, 2026-08-27): CHROME ONLY. Board vocabulary from
// lib/flux/board.js. NO DATA LOGIC TOUCHED: every fetch, useState, useMemo,
// useRef, debounce, handler and query string is byte-identical to v1.1 --
// including the FILTER_DEBOUNCE_MS timer, queryString, the changed-keys-only
// diff in EditDrawer, and the DELETE body in remove(). chipClass() now
// delegates to the board's pill(); it was and remains presentation only.
// The global .card / .input / .label-dark / .btn-* classes were replaced
// INLINE -- index.css is shared with pages outside this pass.
// =============================================================================
// The coach directory (Directory tab) and bounced management (Bounced tab).
//
// ⚠ THIS PAGE'S FIRST LOAD HITS 8,042 REAL COACH ROWS (T-17). Every default is
// a SERVER page: the table renders `contacts` from GET /contacts/search and the
// paginator is driven by the server's {total, limit, offset}. NOTHING on this
// page computes a count, and there is no "fetch all and filter" anywhere in it.
//
// THE DIRECTORY IS SHARED, NOT RECRUIT-SCOPED (T-10). Every role browses it;
// campaign scope is never applied to /contacts/*. Only the destructive and
// identity-bearing actions are gated, and the server gates them independently.
// =============================================================================

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { useMe } from '../../lib/flux/useMe.js';
import { can } from '../../lib/can.js';
import TabBar from '../../components/TabBar.jsx';
import Toast from '../../components/Toast.jsx';
import ConfirmModal from '../../components/ConfirmModal.jsx';
import {
  PAGE, SHELL, PANEL, DASH, BTN_LIGHT, BTN_DARK, BTN_GHOST,
  pill, FIELD_BOX, H1, H2, LABEL, BODY_MUTED,
} from '../../lib/flux/board.js';

const PAGE_SIZE = 50;
const BOUNCED_PAGE_SIZE = 500;
const FILTER_DEBOUNCE_MS = 400;

const TABS = [
  { id: 'directory', label: 'Directory' },
  { id: 'bounced', label: 'Bounced' },
];

// A small inline action inside a table row — ink text, not a pill, so a row of
// three does not read as a toolbar.
const ROW_ACT = 'text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-[#0E2A20] hover:underline disabled:text-[#0E2A20]/30 disabled:no-underline';

// The PATCH allow-list, mirrored from the server route. Identity fields are
// listed separately because they carry the Ruling #8 gate.
const EDITABLE_FIELDS = [
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'title', label: 'Title' },
  { key: 'school', label: 'School' },
  { key: 'division', label: 'Division' },
  { key: 'conference', label: 'Conference' },
  { key: 'state', label: 'State' },
  { key: 'city', label: 'City' },
  { key: 'region', label: 'Region' },
  { key: 'phone', label: 'Phone' },
  { key: 'twitter', label: 'X / Twitter' },
  { key: 'website', label: 'Website' },
];

function chipClass(active) {
  return pill(active);
}

export default function CampaignContacts() {
  const navigate = useNavigate();
  const { role, loading: meLoading } = useMe();

  const [tab, setTab] = useState('directory');
  const [toast, setToast] = useState(null);

  // ── The replace fence (Codex). While an import is rewriting the directory,
  // contact actions are paused and the reason is SHOWN. Read from the run
  // ledger W-3 already owns — no new endpoint.
  const [replaceRunning, setReplaceRunning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/api/flux/contacts/import/runs?limit=5')
      .then((body) => {
        if (cancelled) return;
        const live = (body?.runs || []).some((r) => r.status === 'running' && r.mode === 'replaceAll');
        setReplaceRunning(live);
      })
      .catch(() => { /* the fence is a courtesy; the server refuses regardless */ });
    return () => { cancelled = true; };
  }, []);

  const fenceNote = 'An import is rewriting the directory right now. Contact actions are paused until it finishes.';

  return (
    <div className={PAGE}>
      <div className={`${SHELL} py-10`}>
        <div className="flex items-start justify-between gap-6 mb-8">
          <div>
            <h1 className={H1}>Contacts</h1>
            <p className={`${BODY_MUTED} mt-2`}>The shared coach directory</p>
          </div>
          <div className="flex items-center gap-4 shrink-0 pt-3">
            {can(role, 'contacts.import') && (
              <button type="button" onClick={() => navigate('/contacts/import')} className={BTN_LIGHT}>
                Import contacts
              </button>
            )}
            <button type="button" onClick={() => navigate('/')} className={BTN_GHOST}>
              ← Front Office
            </button>
          </div>
        </div>

        {replaceRunning && (
          <div className="mb-6 bg-amber-50 border-2 border-amber-500 text-amber-900 rounded-2xl px-5 py-4 text-sm">
            {fenceNote}
          </div>
        )}

        <TabBar tabs={TABS} activeTab={tab} onSelect={setTab} variant="board" />

        <div className="mt-8">
          {meLoading ? (
            <div className={`${BODY_MUTED} py-16 text-center`}>Loading…</div>
          ) : tab === 'directory' ? (
            <DirectoryTab role={role} onToast={setToast} fenced={replaceRunning} fenceNote={fenceNote} />
          ) : (
            <BouncedTab role={role} onToast={setToast} fenced={replaceRunning} fenceNote={fenceNote} />
          )}
        </div>
      </div>

      <Toast message={toast?.message} tone={toast?.tone} onDismiss={() => setToast(null)} />
    </div>
  );
}

// ─── Directory ───────────────────────────────────────────────────────────────

function DirectoryTab({ role, onToast, fenced, fenceNote }) {
  const [options, setOptions] = useState({ divisions: [], states: [], regions: [], conferences: [] });
  const [filters, setFilters] = useState({ division: [], state: [], region: [] });
  const [text, setText] = useState({ school: '', title: '', conference: '' });
  const [page, setPage] = useState({ contacts: [], total: 0, limit: PAGE_SIZE, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const debounce = useRef(null);

  useEffect(() => {
    // Server RPCs, never a client-side dedup over the directory (the 1000-row
    // law is why those RPCs exist).
    api.get('/api/flux/contacts/filters').then(setOptions).catch(() => {});
  }, []);

  const queryString = useMemo(() => {
    const q = new URLSearchParams();
    for (const key of ['division', 'state', 'region']) {
      if (filters[key].length) q.set(key, filters[key].join(','));
    }
    for (const key of ['school', 'title', 'conference']) {
      if (text[key].trim()) q.set(key, text[key].trim());
    }
    q.set('limit', String(PAGE_SIZE));
    q.set('offset', String(page.offset));
    return q.toString();
  }, [filters, text, page.offset]);

  const load = useCallback((qs) => {
    setLoading(true);
    setError(null);
    api.get(`/api/flux/contacts/search?${qs}`)
      .then((body) => setPage((prev) => ({ ...body, offset: body.offset ?? prev.offset })))
      .catch((err) => setError(err.body?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(queryString), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(debounce.current);
  }, [queryString, load]);

  function toggle(kind, value) {
    setPage((p) => ({ ...p, offset: 0 }));
    setFilters((f) => ({
      ...f,
      [kind]: f[kind].includes(value) ? f[kind].filter((v) => v !== value) : [...f[kind], value],
    }));
  }

  async function rowAction(contact, patch, successMessage) {
    try {
      await api.patch(`/api/flux/contacts/${contact.id}`, patch);
      onToast({ message: successMessage, tone: 'success' });
      load(queryString);
    } catch (err) {
      // The server's own message, verbatim — a 403 says why in its own words.
      onToast({ message: err.body?.error || err.message, tone: 'error' });
    }
  }

  async function suppress(contact) {
    try {
      await api.post('/api/flux/contacts/flag-bounced', {
        email: contact.email, reason: 'Suppressed by staff',
      });
      onToast({ message: `${contact.email} suppressed.`, tone: 'success' });
      load(queryString);
    } catch (err) {
      onToast({ message: err.body?.error || err.message, tone: 'error' });
    }
  }

  const from = page.total === 0 ? 0 : page.offset + 1;
  const to = Math.min(page.offset + page.limit, page.total);

  return (
    <div>
      <div className={`${PANEL} p-6 mb-6 space-y-4`}>
        <ChipRow label="Division" values={options.divisions} selected={filters.division} onToggle={(v) => toggle('division', v)} />
        <ChipRow label="Region" values={options.regions} selected={filters.region} onToggle={(v) => toggle('region', v)} />
        <ChipRow label="State" values={options.states} selected={filters.state} onToggle={(v) => toggle('state', v)} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {['school', 'title', 'conference'].map((key) => (
            <input
              key={key}
              className={FIELD_BOX}
              placeholder={key[0].toUpperCase() + key.slice(1)}
              value={text[key]}
              onChange={(e) => { setPage((p) => ({ ...p, offset: 0 })); setText((t) => ({ ...t, [key]: e.target.value })); }}
            />
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-700 text-red-800 rounded-2xl px-5 py-4 text-sm mb-6">{error}</div>
      )}

      {loading ? (
        <div className={`${BODY_MUTED} py-16 text-center`}>Loading…</div>
      ) : page.contacts.length === 0 ? (
        <div className={`${BODY_MUTED} py-16 text-center`}>No coaches match those filters.</div>
      ) : (
        <div className={`${PANEL} p-5 overflow-x-auto`}>
          <table className="w-full text-sm">
            <thead>
              <tr className={`text-left ${LABEL} border-b-2 border-dashed border-[#0E2A20]`}>
                <th className="py-3 pr-3">School</th>
                <th className="py-3 pr-3">Name</th>
                <th className="py-3 pr-3">Title</th>
                <th className="py-3 pr-3">Div</th>
                <th className="py-3 pr-3">Conference</th>
                <th className="py-3 pr-3">State / City</th>
                <th className="py-3 pr-3">TZ</th>
                <th className="py-3 pr-3">Email</th>
                <th className="py-3 pr-3">Flags</th>
                <th className="py-3" />
              </tr>
            </thead>
            <tbody>
              {page.contacts.map((c) => (
                <tr key={c.id} className="border-b border-[#0E2A20]/15 text-[#0E2A20] hover:bg-[#F4F4F2]">
                  <td className="py-3 pr-3">{c.school || '—'}</td>
                  <td className="py-3 pr-3">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                  <td className="py-3 pr-3">{c.title || '—'}</td>
                  <td className="py-3 pr-3">{c.division || '—'}</td>
                  <td className="py-3 pr-3">{c.conference || '—'}</td>
                  <td className="py-3 pr-3">{[c.state, c.city].filter(Boolean).join(' / ') || '—'}</td>
                  <td className="py-3 pr-3">{c.timezone || '—'}</td>
                  <td className="py-3 pr-3">{c.email}</td>
                  <td className="py-3 pr-3">
                    {c.unsubscribed_at && (
                      <span className="px-2 py-0.5 rounded-full text-[0.55rem] font-bold uppercase tracking-[0.2em] border-2 border-[#5A5A5A] text-[#5A5A5A]">
                        UNSUBSCRIBED
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    <button type="button" className={`${ROW_ACT} mr-3`} onClick={() => setEditing(c)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className={`${ROW_ACT} mr-3`}
                      disabled={fenced}
                      title={fenced ? fenceNote : undefined}
                      onClick={() => suppress(c)}
                    >
                      Suppress
                    </button>
                    <button
                      type="button"
                      className={ROW_ACT}
                      disabled={!!c.unsubscribed_at}
                      onClick={() => rowAction(c, { unsubscribed_at: new Date().toISOString() },
                        `${c.email} marked unsubscribed.`)}
                    >
                      Mark unsubscribed
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={`flex items-center justify-between mt-6 ${BODY_MUTED}`}>
        <span>Showing {from}–{to} of {page.total}</span>
        <div className="flex gap-3">
          <button
            type="button" className={`${BTN_LIGHT} disabled:opacity-40 disabled:shadow-none`} disabled={page.offset === 0}
            onClick={() => setPage((p) => ({ ...p, offset: Math.max(0, p.offset - PAGE_SIZE) }))}
          >
            Prev
          </button>
          <button
            type="button" className={`${BTN_LIGHT} disabled:opacity-40 disabled:shadow-none`} disabled={page.offset + page.limit >= page.total}
            onClick={() => setPage((p) => ({ ...p, offset: p.offset + PAGE_SIZE }))}
          >
            Next
          </button>
        </div>
      </div>

      {editing && (
        <EditDrawer
          contact={editing}
          role={role}
          onClose={() => setEditing(null)}
          onSaved={(message) => { setEditing(null); onToast({ message, tone: 'success' }); load(queryString); }}
          onError={(message) => onToast({ message, tone: 'error' })}
        />
      )}
    </div>
  );
}

function ChipRow({ label, values, selected, onToggle }) {
  if (!values?.length) return null;
  return (
    <div>
      <div className={`${LABEL} mb-2`}>{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {values.map((v) => (
          <button key={v} type="button" className={chipClass(selected.includes(v))} onClick={() => onToggle(v)}>
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── The edit drawer ─────────────────────────────────────────────────────────
// Identity fields render READ-ONLY WITH A LOCK HINT for non-privileged roles —
// VISIBLE, NOT HIDDEN, so staff can see the field exists and know who to ask.
// Save sends ONLY CHANGED KEYS; the route rejects an empty set.
function EditDrawer({ contact, role, onClose, onSaved, onError }) {
  const [form, setForm] = useState(() => {
    const seed = {};
    for (const f of EDITABLE_FIELDS) seed[f.key] = contact[f.key] ?? '';
    seed.email = contact.email ?? '';
    return seed;
  });
  const [saving, setSaving] = useState(false);
  const privileged = can(role, 'contacts.edit_identity');

  const changed = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(form)) {
      const original = contact[k] ?? '';
      if (String(v) !== String(original)) out[k] = v === '' ? null : v;
    }
    return out;
  }, [form, contact]);

  async function save() {
    if (!Object.keys(changed).length) { onClose(); return; }
    setSaving(true);
    try {
      await api.patch(`/api/flux/contacts/${contact.id}`, changed);
      onSaved(`${contact.email} updated.`);
    } catch (err) {
      onError(err.body?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-[#0E2A20]/30" role="dialog" aria-modal="true">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-7 border-l-2 border-[#0E2A20]">
        <div className="flex items-center justify-between mb-6">
          <h2 className={H2}>Edit contact</h2>
          <button
            type="button" onClick={onClose}
            className="w-7 h-7 leading-none rounded-full border-2 border-[#0E2A20] text-[#0E2A20] hover:bg-[#0E2A20] hover:text-white transition-colors"
          >×</button>
        </div>

        <div className="space-y-4">
          <div>
            <div className={`${LABEL} mb-2`}>Email</div>
            <input
              className={`${FIELD_BOX} w-full`} value={form.email} readOnly={!privileged}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            {!privileged && (
              <p className={`${BODY_MUTED} text-xs mt-2`}>
                🔒 Changing a coach&apos;s email address is admin/manager-only.
              </p>
            )}
          </div>

          {EDITABLE_FIELDS.map((f) => (
            <div key={f.key}>
              <div className={`${LABEL} mb-2`}>{f.label}</div>
              <input
                className={`${FIELD_BOX} w-full`} value={form[f.key] ?? ''}
                onChange={(e) => setForm((x) => ({ ...x, [f.key]: e.target.value }))}
              />
            </div>
          ))}

          {(contact.bounced_at || contact.unsubscribed_at) && (
            <div className={`${DASH} pt-4 text-xs ${BODY_MUTED}`}>
              {contact.bounced_at && <div>Bounced: {contact.bounced_at}</div>}
              {contact.unsubscribed_at && <div>Unsubscribed: {contact.unsubscribed_at}</div>}
              {!privileged && <div className="mt-1">🔒 Clearing these stamps is admin/manager-only.</div>}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <button type="button" onClick={onClose} className={BTN_LIGHT}>Cancel</button>
          <button type="button" onClick={save} disabled={saving} className={`${BTN_DARK} disabled:opacity-40 disabled:shadow-none`}>
            {saving ? 'Saving…' : `Save${Object.keys(changed).length ? ` (${Object.keys(changed).length})` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bounced ─────────────────────────────────────────────────────────────────
// RM's "Remove Selected" hard delete is CUT AS PRIMARY. Restore, Suppress and
// Mark-unsubscribed come first; Remove is admin-only, behind a TYPED
// confirmation, and its copy tells the whole truth — RM's confirm() did not.
function BouncedTab({ role, onToast, fenced, fenceNote }) {
  const [page, setPage] = useState({ contacts: [], total: 0, limit: BOUNCED_PAGE_SIZE, offset: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback((offset = 0) => {
    setLoading(true);
    api.get(`/api/flux/contacts/bounced?limit=${BOUNCED_PAGE_SIZE}&offset=${offset}`)
      .then((body) => { setPage(body); setSelected(new Set()); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(0); }, [load]);

  async function restore(contact) {
    try {
      await api.patch(`/api/flux/contacts/${contact.id}`, { bounced_at: null, bounce_reason: null });
      onToast({ message: `${contact.email} restored to active.`, tone: 'success' });
      load(page.offset);
    } catch (err) {
      onToast({ message: err.body?.error || err.message, tone: 'error' });
    }
  }

  async function remove() {
    try {
      const body = await api(`/api/flux/contacts/bounced/remove`, {
        method: 'DELETE',
        body: JSON.stringify({ contactIds: [...selected] }),
      });
      onToast({
        message: `Removed ${body.removed} contact row(s); ${body.dependent_rows_suppressed} dependent message row(s) suppressed.`,
        tone: 'success',
      });
      setConfirmOpen(false);
      load(page.offset);
    } catch (err) {
      onToast({ message: err.body?.error || err.message, tone: 'error' });
      setConfirmOpen(false);
    }
  }

  if (loading) return <div className={`${BODY_MUTED} py-16 text-center`}>Loading…</div>;
  if (!page.contacts.length) return <div className={`${BODY_MUTED} py-16 text-center`}>No bounced contacts.</div>;

  return (
    <div>
      {can(role, 'contacts.remove') && (
        <div className="flex justify-end mb-5">
          <button
            type="button"
            className={`${BTN_LIGHT} border-red-700 text-red-800 shadow-[0_4px_0_#b91c1c] active:shadow-[0_2px_0_#b91c1c] disabled:opacity-40 disabled:shadow-none`}
            disabled={selected.size === 0 || fenced}
            title={fenced ? fenceNote : undefined}
            onClick={() => setConfirmOpen(true)}
          >
            Remove Selected ({selected.size})
          </button>
        </div>
      )}

      <div className={`${PANEL} p-5 overflow-x-auto`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`text-left ${LABEL} border-b-2 border-dashed border-[#0E2A20]`}>
              {can(role, 'contacts.remove') && <th className="py-3 pr-3" />}
              <th className="py-3 pr-3">Email</th>
              <th className="py-3 pr-3">Name</th>
              <th className="py-3 pr-3">School</th>
              <th className="py-3 pr-3">Div</th>
              <th className="py-3 pr-3">Bounced</th>
              <th className="py-3 pr-3">Reason</th>
              <th className="py-3" />
            </tr>
          </thead>
          <tbody>
            {page.contacts.map((c) => (
              <tr key={c.id} className="border-b border-[#0E2A20]/15 text-[#0E2A20]">
                {can(role, 'contacts.remove') && (
                  <td className="py-3 pr-3">
                    <input
                      type="checkbox" checked={selected.has(c.id)} className="accent-[#0E2A20]"
                      onChange={(e) => setSelected((s) => {
                        const next = new Set(s);
                        if (e.target.checked) next.add(c.id); else next.delete(c.id);
                        return next;
                      })}
                    />
                  </td>
                )}
                <td className="py-3 pr-3">{c.email}</td>
                <td className="py-3 pr-3">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                <td className="py-3 pr-3">{c.school || '—'}</td>
                <td className="py-3 pr-3">{c.division || '—'}</td>
                <td className="py-3 pr-3">{c.bounced_at}</td>
                <td className="py-3 pr-3">{c.bounce_reason || '—'}</td>
                <td className="py-3 text-right">
                  <button
                    type="button" className={ROW_ACT}
                    disabled={fenced} title={fenced ? fenceNote : undefined}
                    onClick={() => restore(c)}
                  >
                    Restore to active
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={confirmOpen}
        destructive
        requireTyped="REMOVE"
        title="Remove bounced contacts"
        body={`Removes ${selected.size} contact row(s). Their queued messages are suppressed first, and the addresses stay dead in the suppression list — a future import cannot bring them back clean.`}
        confirmLabel="Remove"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={remove}
      />
    </div>
  );
}
